import React from 'react';
import styled from 'styled-components';
import { Fade } from 'react-awesome-reveal';

interface PageContainerProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  /** Optional data-testid applied to the outermost Container element. */
  'data-testid'?: string;
}

const Container = styled.div``;

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const TitleSection = styled.div`
  @media (max-width: 768px) {
    margin-bottom: ${({ theme }) => theme.spacing.sm};
  }
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: ${({ theme }) => theme.typography.fontSizes.xl};
  }
  
  @media (max-width: 480px) {
    font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  }
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: ${({ theme }) => theme.spacing.xs} 0 0;
  
  @media (max-width: 480px) {
    font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    width: 100%;
    
    > * {
      flex: 1;
      min-width: 120px;
    }
  }
  
  @media (max-width: 480px) {
    gap: ${({ theme }) => theme.spacing.xs};
  }
`;

const Content = styled.div``;

export const PageContainer: React.FC<PageContainerProps> = ({
  title,
  subtitle,
  actions,
  children,
  'data-testid': testId,
}) => {
  return (
    <Container data-testid={testId}>
      <Fade triggerOnce duration={300}>
        <PageHeader>
          <TitleSection>
            <Title>{title}</Title>
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
          </TitleSection>
          {actions && <Actions>{actions}</Actions>}
        </PageHeader>
      </Fade>
      <Fade triggerOnce duration={300} delay={100}>
        <Content>{children}</Content>
      </Fade>
    </Container>
  );
};

